import { getInstance as getBootstrap } from 'utils/bootstrap';
import thirdPartyTrackingManager from 'lib/thirdPartyTrackingManager';
import { getUserCookieConsent } from 'common/compounds/CookieManagement/cookieTools';
import { COOKIE_CATEGORY } from 'common/constants/cookieManagement';

/**
 * The initializer function called when the app is loaded.
 * The function is:
 * - Open to configuration via window.thirdPartyTrackingManagerObject:
 *     - debug: boolean - whether to enable debug mode
 *     - version: string - the version of the app
 *     - app: string - the name of the app
 * - Isomorphic
 */
(async function () {
    const eventId = getBootstrap().get('splashPage.eventId');

    if (typeof window !== 'undefined') {
        const thirdPartyTrackingManagerLib = await thirdPartyTrackingManager.init({
            eventId,
        });

        /**
         * Instantiates the third-party tracking manager with the provided consent categories.
         * Creates or updates the global thirdPartyTrackingManager instance with analytics configuration.
         *
         * @async
         * @function instantiateThirdPartyTrackingManager
         * @param {string[]} cookieConsentedCategories - Array of cookie categories user has consented to.
         *   Categories include: 'performance', 'functionality', 'marketing'
         * @returns {Promise<void>} Promise that resolves when tracking manager is instantiated
         *
         * @example
         * // Initialize with all categories
         * await window.instantiateThirdPartyTrackingManager(['performance', 'functionality', 'marketing']);
         *
         * @example
         * // Initialize with limited consent
         * await window.instantiateThirdPartyTrackingManager(['performance']);
         */
        window.instantiateThirdPartyTrackingManager = async (cookieConsentedCategories) => {
            window.thirdPartyTrackingManager = {
                ...(window.thirdPartyTrackingManager || {}),
                instance: await thirdPartyTrackingManagerLib.getAnalyticsInstance({
                    app: window.thirdPartyTrackingManager?.app || 'splash-tracking-management-system',
                    debug: window.thirdPartyTrackingManager?.debug,
                    version: window.thirdPartyTrackingManager?.version || '1.0.0',
                    cookieConsentedCategories,
                }),
            };
        };

        if (!window.thirdPartyTrackingManager?.instance) {
            const { enableCookieBanner } = getBootstrap().get('cookieSettings', {});

            let cookieConsentedCategories = [];

            if (!enableCookieBanner) {
                // No cookie banner - allow all tracking categories
                cookieConsentedCategories = [
                    COOKIE_CATEGORY.PERFORMANCE,
                    COOKIE_CATEGORY.FUNCTIONALITY,
                    COOKIE_CATEGORY.MARKETING,
                ];
            } else {
                // Cookie banner enabled - only use consented categories
                const localCookieConsent = getUserCookieConsent(eventId);
                cookieConsentedCategories = Object.entries(localCookieConsent || {})
                    .filter(([, consented]) => consented)
                    .map(([category]) => category);
            }

            await window.instantiateThirdPartyTrackingManager(cookieConsentedCategories);

            const customInitialPageEventConfiguration = {
                plugins: {
                    'analytics-plugin-marketo-munchkin': false,
                },
            };

            window.thirdPartyTrackingManager.instance.page({}, customInitialPageEventConfiguration);
        }
    }
})();
